/**
 * The tile side of `.pxm`: an authored room unit, and the measurement that says
 * whether it is one.
 *
 * ==== WHAT AN AUTHORED TILE IS ==========================================
 *
 * `docs/19` §6 answers the question this directory exists for, and answers it
 * with Noita's own asset names rather than with an inference: *"a pixel scene is
 * exactly one wang tile — the tile is the room"*. Their `coalmine` horizontal
 * pool is seventeen files called `shrine01`, `slimepit`, `laboratory`, `shop`,
 * `radioactivecave`; an observatory is not a fragment. And their changelog
 * defines `static_tile` biomes as *"wang biomes without randomization,
 * consisting of one large tile"* — the tile is the room either way, and
 * randomisation only chooses which room.
 *
 * `src/world/rooms.ts` has been a herringbone Wang tiler after Sean Barrett
 * since long before any of this was measured, and its header records that
 * Noita's team moved to herringbone for the same reason we did. **The tiler
 * existed and the tiles did not.** This is one tile.
 *
 * ==== WHY A DRAWN ROOM DROPS IN WITHOUT A SOLVER ========================
 *
 * Two properties of `rooms.ts` make an authored tile cheap here, and both are
 * asserted in `tests/tileMask.test.ts` rather than trusted:
 *
 *  1. **Edge matching is derived, not searched.** `rooms.ts`'s header: *"the
 *     edge is the primitive. Each unit edge's connection type is a pure hash of
 *     the edge's own coordinates, so two tiles sharing an edge always agree
 *     without communicating."* Nothing selects a tile *to fit* its neighbours,
 *     so nothing has to re-run when a slot's occupant changes. An authored tile
 *     is a substitute for the *interior* of a slot and the six exterior edges
 *     are drawn over it afterwards by `edgeWalls`, unchanged — which means one
 *     drawn room is legal at every one of the 3^6 edge signatures, without a
 *     variant per signature and without a single backtrack.
 *
 *  2. **The three-way classification already exists.** `Kind.OPEN` is air,
 *     `Kind.SOLID` is "the rock around here", and `SHELL`/`PLATFORM`/`GATE` are
 *     authored material. Issue #143 wired the *resolution* — `Kind.SOLID` now
 *     answers one rock per `MATERIAL_SPAN` region instead of a different rock
 *     every few cells — but the vocabulary was already there, which is why this
 *     file has no material in it anywhere.
 *
 * ==== WHY THE FILE NAMES NO MATERIAL ====================================
 *
 * `kindGrid` refuses one. A tile is drawn in **deferred classes** — `@host`,
 * `@shell`, `@platform`, `@gate` — and the host biome supplies every colour, so
 * the same drawn room is olive rock in the Rotwood and grey rock in the
 * Coldforge and reads as native in both. That is Noita's `#ffffff` and
 * Starbound's grey pixel, which `docs/17` §2.5 item 2 found to be the one
 * unanimous convergence across five studied generators.
 *
 * The refusal is not a limitation waiting to be lifted, it is the other half of
 * `specFor`'s: between the two resolvers, every legend value has exactly one
 * consumer that accepts it, and a document that goes to the wrong one says so
 * instead of writing a hole.
 */

import {
  DEFERRED_CLASSES,
  DEFERRED_PREFIX,
  VOID_CHAR,
  deferredClassOf,
  type DeferredClass,
  type ShapeAnchor,
  type ShapeMaskDoc,
} from '../../sim/shapeMask';

class TileMaskError extends Error {}

/**
 * Resolve a document into a grid of cell classes.
 *
 * `resolve` is passed in for the reason `specFor`'s is: the class-to-`Kind`
 * table belongs to `rooms.ts`, which owns `Kind`, and importing it here would
 * make the pair circular for no gain. `air` is what `VOID_CHAR` becomes —
 * `Kind.OPEN`, and the `.` in a tile means exactly what it means in a rigid
 * mask, *nothing is here*.
 *
 * A material name fails, with the tile's name attached. See the header.
 *
 * **A pool character (`doc.pools`, issue #183) writes `air` here and nowhere
 * else reads it.** This buffer is built once per tile at load time, and a
 * pool has no seed to roll against yet — the seed only exists once a slot is
 * being stamped. `rooms.ts`'s `stampAuthored` rolls each pool once per
 * placement and overwrites every one of its cells before this buffer's value
 * at that cell is ever read, so the value written here is dead data; `air` is
 * used only because it needs no lookup, not because it means anything.
 */
export function kindGrid(
  doc: ShapeMaskDoc,
  resolve: (cls: DeferredClass) => number | undefined,
  air: number,
): Uint8Array {
  const byChar = new Map<string, number>();
  for (const [ch, value] of doc.legend) {
    const cls = deferredClassOf(value);
    if (cls === undefined) {
      // The vocabulary is spelled out of `DEFERRED_CLASSES` rather than typed
      // here. A second copy of the list is a second thing to forget when the
      // list grows — which it did, from four to seven — and the message that
      // says which words are legal is the one place a stale copy is invisible.
      throw new TileMaskError(
        `tile ${doc.name}: \`${value}\` is a material, and a tile is drawn in classes ` +
          `(${DEFERRED_CLASSES.map((c) => DEFERRED_PREFIX + c).join(', ')}) ` +
          'so the host biome supplies every colour',
      );
    }
    const k = resolve(cls);
    if (k === undefined) throw new TileMaskError(`tile ${doc.name}: no cell class for \`${value}\``);
    byChar.set(ch, k);
  }
  const poolChars = new Set(doc.pools.map((p) => p.ch));
  const cells = new Uint8Array(doc.w * doc.h);
  for (let y = 0; y < doc.h; y++) {
    const row = doc.rows[y];
    for (let x = 0; x < doc.w; x++) {
      const ch = row[x];
      cells[y * doc.w + x] = ch === VOID_CHAR || poolChars.has(ch) ? air : (byChar.get(ch) as number);
    }
  }
  return cells;
}

/** An anchor after placement: the same anchor, at world cells. */
export interface PlacedAnchor extends ShapeAnchor {
  /** World x of the anchored cell. */
  readonly wx: number;
  /** World y of the anchored cell. */
  readonly wy: number;
}

/**
 * Where a drawing's anchors land once the lattice has put the drawing somewhere.
 *
 * One line of arithmetic, and it lives here rather than at every call site
 * because **the convention is the thing worth stating once**: anchor
 * coordinates are grid-local, origin at the drawing's top-left cell, `+y` down —
 * the grid's own convention, so an anchor is read off the file by counting — and
 * a slot's origin is the world cell that top-left corner is stamped at. A second
 * copy of that sum, off by the tile's height because somebody assumed `+y` up,
 * is a spawner in the ceiling.
 *
 * This is the seam an anchor's *world*-level claims are checked across, and the
 * two halves are deliberately different in kind. `floor` and `headroom` are true
 * of the drawing and the parser has already guaranteed them
 * (`docs/29-pxm-format.md` §4.4). Whether the placed cell is still dry, still
 * reachable, still unburied by a mega mass is a fact about the *world* — the
 * same four-clause question `tests/greatTree.test.ts` asks of
 * `TREE_HOLLOW_SITE` — and it can only be asked after this function has said
 * where to look.
 */
export function anchorsInWorld(doc: ShapeMaskDoc, originX: number, originY: number): PlacedAnchor[] {
  return doc.anchors.map((a) => ({ ...a, wx: originX + a.x, wy: originY + a.y }));
}

/** What one authored tile is made of, as fractions of its own area. */
export interface TileComposition {
  /** `VOID_CHAR`. Noita's `#000000`. */
  air: number;
  /** `@host`. Noita's `#ffffff`. */
  host: number;
  /** Everything else — `@shell`, `@platform`, `@gate`. Noita's explicit colours. */
  authored: number;
  /** The share of the outermost one-cell ring that is `@host`. */
  borderHost: number;
  /** Legend entries plus air, which is what Noita's "distinct colours" counts. */
  distinct: number;
  /** Cell counts, for a report that can be checked by hand. */
  cells: { air: number; host: number; authored: number; total: number };
}

/**
 * **The recipe, from `docs/19` §3.2, as a sentence:** *an authored Noita cave
 * room is ~50 % air, ~30 % host-biome rock, ~15 % authored material, drawn with
 * a median of 4–8 distinct colours, with a mostly-host-rock border.*
 *
 * These are the medians over 219 measured scenes and they are **targets, not
 * bars**. Nothing throws on them. `tests/tileMask.test.ts` prints the gap and
 * holds only the two that are structural rather than stylistic — the border,
 * because a border that is not host rock is a visible seam and is the whole
 * mechanism, and the palette count, because it is the one number in the row
 * with a range rather than a point.
 */
export const NOITA_COMPOSITION = {
  air: 0.483,
  host: 0.30,
  authored: 0.15,
  /** §3.2: 36–82 % of the 1-px ring across the four cave biomes; §8.3 asks ≥ 60 %. */
  borderHost: 0.60,
  distinctMin: 4,
  distinctMax: 8,
} as const;

/**
 * Measure a tile against the recipe.
 *
 * Classified **by exclusion**, deliberately, and it is the shape
 * `tests/hostFill.test.ts` uses for the same reason: `authored` is "not air and
 * not host" rather than "the three classes I remembered to list", so a class
 * added to the format later lands in the measurement without anyone updating
 * this function, and a mutation that reclassifies a cell cannot hide in the
 * gap between two lists.
 */
export function composition(doc: ShapeMaskDoc): TileComposition {
  const hostChars = new Set<string>();
  for (const [ch, value] of doc.legend) {
    if (deferredClassOf(value) === 'host') hostChars.add(ch);
  }
  const total = doc.w * doc.h;
  let air = 0;
  let host = 0;
  for (const row of doc.rows) {
    for (const ch of row) {
      if (ch === VOID_CHAR) air++;
      else if (hostChars.has(ch)) host++;
    }
  }
  const authored = total - air - host;

  let ring = 0;
  let ringHost = 0;
  for (let y = 0; y < doc.h; y++) {
    for (let x = 0; x < doc.w; x++) {
      if (x !== 0 && y !== 0 && x !== doc.w - 1 && y !== doc.h - 1) continue;
      ring++;
      if (hostChars.has(doc.rows[y][x])) ringHost++;
    }
  }

  return {
    air: air / total,
    host: host / total,
    authored: authored / total,
    borderHost: ring === 0 ? 0 : ringHost / ring,
    distinct: doc.legend.length + (air > 0 ? 1 : 0),
    cells: { air, host, authored, total },
  };
}

/** One line per figure, for `pixel tile check` and for a test that prints the gap. */
export function compositionReport(name: string, c: TileComposition): string[] {
  const pc = (v: number): string => `${(100 * v).toFixed(1)}%`.padStart(6);
  return [
    `${name}: ${c.cells.total} cells, ${c.distinct} distinct classes ` +
      `(Noita's median is ${NOITA_COMPOSITION.distinctMin}-${NOITA_COMPOSITION.distinctMax})`,
    `  air       ${pc(c.air)}   target ${pc(NOITA_COMPOSITION.air)}   (${c.cells.air} cells)`,
    `  host fill ${pc(c.host)}   target ${pc(NOITA_COMPOSITION.host)}   (${c.cells.host} cells)`,
    `  authored  ${pc(c.authored)}   target ${pc(NOITA_COMPOSITION.authored)}   (${c.cells.authored} cells)`,
    `  border    ${pc(c.borderHost)}   floor  ${pc(NOITA_COMPOSITION.borderHost)}  host rock in the 1-cell ring`,
  ];
}
